import { OnDestroy, OnInit } from "@angular/core";
import { NavController } from "ionic-angular";
import { NavParams } from "ionic-angular/navigation/nav-params";
import { Subscription } from "rxjs";
import { forkJoin } from "rxjs/observable/forkJoin";
import { AccessControlService } from "../../app/access-control/access-control-service";
import { RESOURCE_MANAGEMENT_PAGE } from "../../app/access-control/common-permission-asks";
import LookupValue from "../../models/patient-chart/lookupValue";
import VolunteerMatchTerm from "../../models/resource-management/volunteers/volunteer-match-term";
import { NavBarServiceProvider } from "../../providers/nav-bar-service/nav-bar-service";
import { PatientChartService } from "../../providers/patient-charts/patient-chart.service";
import { ResourceManagementService } from "../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../providers/state-management/state-management.service";
import { UsersService } from "../../providers/users-service/users.service";
import { BasePage } from "../base-page";

export default class CompanyResourcePage
  extends BasePage
  implements OnDestroy, OnInit
{
  protected accessControlSubscription: Subscription;
  protected domainAccessSubscription: Subscription;
  protected languageLookupSubscription: Subscription;
  /** The ngOnInit lookup forkJoin. It used to be subscribed anonymously, so it was
   *  the one subscription ngOnDestroy could not release (CO-3352). */
  protected lookupSubscription: Subscription;
  public hasEmployeeAccess: boolean = false;
  public hasProviderAccess: boolean = false;
  public hasVolunteerAccess: boolean = false;
  public hasBackOfficeAccess: boolean = false;
  public hasResourceReports: boolean = false;
  public featureToggleProviderFlatUIEnabled: boolean = false;
  public languages: LookupValue[] = [];
  public credentialTypes: LookupValue[] = [];
  public volunteerTypes: LookupValue[] = [];
  public professionalRelations: LookupValue[] = [];
  public volunteerActivityCategories: VolunteerMatchTerm[] = [];

  constructor(
    protected accessControlService: AccessControlService,
    protected navBarSvc: NavBarServiceProvider,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    protected userService: UsersService,
    protected stateManagementService: StateManagementService,
    protected patientChartService: PatientChartService,
    protected resourceManagementService: ResourceManagementService
  ) {
    super(stateManagementService);
  }

  /**
   * Evaluates that the user has access to the areas that matter
   */
  evaluateAccess(): void {
    this.accessControlSubscription = this.accessControlService
      .getAccessControlData(RESOURCE_MANAGEMENT_PAGE)
      .subscribe((x) => {
        // `x.permissions[0].access` was read unguarded. IAM answers with an empty
        // `permissions` array whenever the ask resolves to nothing for the caller, so
        // this threw INSIDE the subscribe callback and aborted the rest of the handler
        // — which is where the two feature toggles below are read. The nav rail then
        // silently lost its Activity Log and Reports entries as well as the access
        // flags, on an error that nothing reported (CO-3352).
        const permission = x && x.permissions ? x.permissions[0] : null;
        let hasAccess = permission ? permission.access : false;
        this.hasEmployeeAccess = hasAccess;
        this.hasProviderAccess = hasAccess;
        this.hasVolunteerAccess = hasAccess;
        this.hasBackOfficeAccess = hasAccess;
        this.featureToggleProviderFlatUIEnabled =
          this.navBarSvc.featureToggleOn("Provider Flat UI");
        this.hasResourceReports =
          this.navBarSvc.featureToggleOn("Resource Reports");
      });
  }

  hasResourceManagementPermission(resource: number, data: any): boolean {
    if (data.domains) {
      for (let i = 0; i < data.domains.length; i++) {
        const j = data.domains[i];
        if (j.resourceAccessList && j.resourceAccessList.length === 1) {
          if (j.resourceAccessList[0].appResourceId === resource) {
            return j.resourceAccessList[0].access > 0;
          }
        }
      }
    }

    return false;
  }

  /**
   * Cleans up subs and whatnot
   */
  ngOnDestroy(): void {
    // super.ngOnDestroy() was missing entirely, so every BasePage feature-toggle
    // subscription (showVolunteerResources, showCommunityBereavement,
    // showPalliativeBilling, showIsPatientEPrescribe, showIsPatientCal,
    // showPalliativeV1_5, showIdgTempChanges, showOrderEntryOM) leaked on each visit
    // to ANY of the six company-resources screens. The subclasses that do override
    // ngOnDestroy call super.ngOnDestroy() — which landed here and stopped, so the
    // leak was invisible from their side (CO-3352).
    super.ngOnDestroy();

    if (this.domainAccessSubscription) {
      this.domainAccessSubscription.unsubscribe();
    }

    if (this.languageLookupSubscription) {
      this.languageLookupSubscription.unsubscribe();
    }

    if (this.accessControlSubscription) {
      this.accessControlSubscription.unsubscribe();
    }

    if (this.lookupSubscription) {
      this.lookupSubscription.unsubscribe();
    }
  }

  /**
   * Inits the page
   */
  ngOnInit(): void {
    const chartLookups = this.patientChartService.getLookUpValues();
    const resourceManagementLookups =
      this.resourceManagementService.getLookUpValues();
    const volunteerActivityLookups =
      this.resourceManagementService.getVolunteerMatchTerms(false);
    this.evaluateAccess();
    this.lookupSubscription = forkJoin(
      chartLookups,
      resourceManagementLookups,
      volunteerActivityLookups
    ).subscribe(
      (results) => {
        this.handleLookupUpdates(results);
      },
      (err) => {
        console.error(err);
      }
    );
  }

  handleLookupUpdates(results: any): void {
    // Every one of these five reads was unguarded. They feed <company-resources-grid>
    // @Input()s, and that component's `set languages()` calls `languages.map(...)`
    // straight away — so a lookup payload missing a key assigned `undefined` here and
    // the child threw on the next change-detection pass, blanking the whole screen
    // rather than just the one filter. Defaulting to [] keeps the contract the grid
    // expects (CO-3352).
    const chartLookups = (results && results[0]) || {};
    const rmLookups = (results && results[1]) || {};
    this.languages = chartLookups.FirstLanguage || [];
    this.professionalRelations = rmLookups.ProfessionalRelationType || [];
    this.credentialTypes = rmLookups.CredentialType || [];
    this.volunteerTypes = rmLookups.VolunteerType || [];
    this.volunteerActivityCategories = (results && results[2]) || [];
  }
}



// WEBPACK FOOTER //
// ./src/pages/company-resources/company-resource-page.ts